import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import apiClient from '@/libs/apiClient'
import type { ProductAttribute, ProductColor, ProductSize } from '@/types/catalog'

interface AttributeState {
  attributes: ProductAttribute[]
  colors: ProductColor[]
  sizes: ProductSize[]
  loading: boolean
  error: string | null
}

const initialState: AttributeState = {
  attributes: [],
  colors: [],
  sizes: [],
  loading: false,
  error: null
}

// ===============================
// General Attributes Thunks
// ===============================
export const fetchAttributes = createAsyncThunk<ProductAttribute[], void, { rejectValue: string }>(
  'attributes/fetchAttributes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/admin/v1/attributes')

      return res.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch attributes')
    }
  }
)

export const addAttribute = createAsyncThunk<ProductAttribute, ProductAttribute, { rejectValue: string }>(
  'attributes/addAttribute',
  async (attribute, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/attributes/add', attribute)

      return res.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add attribute')
    }
  }
)

export const updateAttribute = createAsyncThunk<ProductAttribute, ProductAttribute, { rejectValue: string }>(
  'attributes/updateAttribute',
  async (attribute, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/attributes/${attribute.id}/edit`, attribute)

      return res.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update attribute')
    }
  }
)

export const deleteAttribute = createAsyncThunk<string, string, { rejectValue: string }>(
  'attributes/deleteAttribute',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/attributes/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete attribute')
    }
  }
)

// ===============================
// Colors Thunks
// ===============================
export const fetchColors = createAsyncThunk<ProductColor[], void, { rejectValue: string }>(
  'attributes/fetchColors',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/admin/v1/colors', { params: { limit: 100 } })

      return res.data.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch colors')
    }
  }
)

export const addColor = createAsyncThunk<ProductColor, FormData, { rejectValue: string }>(
  'attributes/addColor',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/colors/add', formData)

      return res.data.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add color')
    }
  }
)

export const updateColor = createAsyncThunk<ProductColor, { id: string; formData: FormData }, { rejectValue: string }>(
  'attributes/updateColor',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/colors/${id}/edit`, formData)

      return res.data.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update color')
    }
  }
)

export const deleteColor = createAsyncThunk<string, string, { rejectValue: string }>(
  'attributes/deleteColor',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/colors/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete color')
    }
  }
)

// ===============================
// Sizes Thunks
// ===============================
export const fetchSizes = createAsyncThunk<ProductSize[], void, { rejectValue: string }>(
  'attributes/fetchSizes',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/admin/v1/sizes', { params: { limit: 100 } })

      return res.data.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch sizes')
    }
  }
)

export const addSize = createAsyncThunk<ProductSize, FormData, { rejectValue: string }>(
  'attributes/addSize',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/sizes/add', formData)

      return res.data.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add size')
    }
  }
)

export const updateSize = createAsyncThunk<ProductSize, { id: string; formData: FormData }, { rejectValue: string }>(
  'attributes/updateSize',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/sizes/${id}/edit`, formData)

      return res.data.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update size')
    }
  }
)

export const deleteSize = createAsyncThunk<string, string, { rejectValue: string }>(
  'attributes/deleteSize',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/sizes/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete size')
    }
  }
)

const attributeSlice = createSlice({
  name: 'attributes',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      // Attributes
      .addCase(fetchAttributes.pending, state => {
        state.loading = true
      })
      .addCase(fetchAttributes.fulfilled, (state, action) => {
        state.loading = false
        state.attributes = action.payload
      })
      .addCase(fetchAttributes.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch attributes'
      })
      .addCase(addAttribute.fulfilled, (state, action) => {
        state.attributes.unshift(action.payload)
        toast.success('Attribute added')
      })
      .addCase(updateAttribute.fulfilled, (state, action) => {
        state.attributes = state.attributes.map(a => (a.id === action.payload.id ? action.payload : a))
        toast.success('Attribute updated')
      })
      .addCase(deleteAttribute.fulfilled, (state, action) => {
        state.attributes = state.attributes.filter(a => a.id !== action.payload)
        toast.success('Attribute deleted')
      })

      // Colors
      .addCase(fetchColors.fulfilled, (state, action) => {
        state.colors = action.payload
      })
      .addCase(addColor.fulfilled, (state, action) => {
        state.colors.unshift(action.payload)
        toast.success('Color added')
      })
      .addCase(updateColor.fulfilled, (state, action) => {
        state.colors = state.colors.map(c => (c.id === action.payload.id ? action.payload : c))
        toast.success('Color updated')
      })
      .addCase(deleteColor.fulfilled, (state, action) => {
        state.colors = state.colors.filter(c => c.id !== action.payload)
        toast.success('Color deleted')
      })

      // Sizes
      .addCase(fetchSizes.fulfilled, (state, action) => {
        state.sizes = action.payload
      })
      .addCase(addSize.fulfilled, (state, action) => {
        state.sizes.unshift(action.payload)
        toast.success('Size added')
      })
      .addCase(updateSize.fulfilled, (state, action) => {
        state.sizes = state.sizes.map(s => (s.id === action.payload.id ? action.payload : s))
        toast.success('Size updated')
      })
      .addCase(deleteSize.fulfilled, (state, action) => {
        state.sizes = state.sizes.filter(s => s.id !== action.payload)
        toast.success('Size deleted')
      })
  }
})

export default attributeSlice.reducer
